/**
 * AgentGlyph
 *
 * Custom SVG marks for each agent type. These are intentionally different
 * from the Lucide line-art icons used everywhere else in the interface:
 *   - They live inside circular gradient containers (not square icon boxes)
 *   - The paths are hand-drawn to evoke each agent's mental model
 *     (radar sweep, mirrored nodes, heartbeat, inversion arrows)
 *   - They have a subtle pulse-ring animation that Lucide icons never get
 *
 * This is the visual language that says "an agent lives here" without
 * needing a label.
 */

import type { AgentGlyphType, AgentColorScheme } from './types';

// ── Color maps ────────────────────────────────────────────────────────────────

const GRADIENT_CLASSES: Record<AgentColorScheme, string> = {
  amber:   'from-amber-400 to-orange-500',
  indigo:  'from-indigo-400 to-violet-500',
  emerald: 'from-emerald-400 to-teal-500',
  rose:    'from-rose-400 to-pink-500',
};

const RING_CLASSES: Record<AgentColorScheme, string> = {
  amber:   'ring-amber-400/40',
  indigo:  'ring-indigo-400/40',
  emerald: 'ring-emerald-400/40',
  rose:    'ring-rose-400/40',
};

// ── SVG paths ─────────────────────────────────────────────────────────────────

/**
 * ATC — Air Traffic Controller
 * Radar sweep: concentric arcs emanating from a center dot, with a sweep arm.
 * Evokes scanning, classifying, orienting.
 */
function AtcGlyph({ size }: { size: number }) {
  const s = size;
  const cx = s / 2;
  const cy = s / 2;
  const r1 = s * 0.18;
  const r2 = s * 0.33;
  const r3 = s * 0.42;

  // Sweep arm angle (NW direction)
  const angle = -135 * (Math.PI / 180);
  const armLen = s * 0.36;
  const ax = cx + Math.cos(angle) * armLen;
  const ay = cy + Math.sin(angle) * armLen;

  return (
    <svg
      viewBox={`0 0 ${s} ${s}`}
      width={s}
      height={s}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Center dot */}
      <circle cx={cx} cy={cy} r={s * 0.075} fill="white" />
      {/* Sweep arm */}
      <line
        x1={cx} y1={cy} x2={ax} y2={ay}
        stroke="white"
        strokeWidth={s * 0.07}
        strokeLinecap="round"
        opacity="0.95"
      />
      {/* Concentric arcs — tightest to widest, fading */}
      <path
        d={`M ${cx + Math.cos(-180 * (Math.PI / 180)) * r1} ${cy + Math.sin(-180 * (Math.PI / 180)) * r1} A ${r1} ${r1} 0 1 1 ${cx + Math.cos(0) * r1} ${cy + Math.sin(0) * r1}`}
        stroke="white"
        strokeWidth={s * 0.065}
        strokeLinecap="round"
        fill="none"
        opacity="0.95"
      />
      <path
        d={`M ${cx + Math.cos((-210) * (Math.PI / 180)) * r2} ${cy + Math.sin((-210) * (Math.PI / 180)) * r2} A ${r2} ${r2} 0 1 1 ${cx + Math.cos((-30) * (Math.PI / 180)) * r2} ${cy + Math.sin((-30) * (Math.PI / 180)) * r2}`}
        stroke="white"
        strokeWidth={s * 0.06}
        strokeLinecap="round"
        fill="none"
        opacity="0.65"
      />
      <path
        d={`M ${cx + Math.cos((-220) * (Math.PI / 180)) * r3} ${cy + Math.sin((-220) * (Math.PI / 180)) * r3} A ${r3} ${r3} 0 1 1 ${cx + Math.cos((-25) * (Math.PI / 180)) * r3} ${cy + Math.sin((-25) * (Math.PI / 180)) * r3}`}
        stroke="white"
        strokeWidth={s * 0.055}
        strokeLinecap="round"
        fill="none"
        opacity="0.35"
      />
    </svg>
  );
}

/**
 * Twins — LLM Twins / synthetic persona modeling
 * Two overlapping circles (Venn-like), connected at their centers.
 * Evokes mirroring, perspective-taking, dual-channel simulation.
 */
function TwinsGlyph({ size }: { size: number }) {
  const s = size;
  const r = s * 0.28;
  const offset = s * 0.18;
  const cx = s / 2;
  const cy = s / 2;

  return (
    <svg
      viewBox={`0 0 ${s} ${s}`}
      width={s}
      height={s}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Left circle */}
      <circle
        cx={cx - offset}
        cy={cy}
        r={r}
        stroke="white"
        strokeWidth={s * 0.07}
        fill="white"
        fillOpacity="0.15"
      />
      {/* Right circle */}
      <circle
        cx={cx + offset}
        cy={cy}
        r={r}
        stroke="white"
        strokeWidth={s * 0.07}
        fill="white"
        fillOpacity="0.15"
      />
      {/* Center connection dot */}
      <circle cx={cx} cy={cy} r={s * 0.055} fill="white" opacity="0.9" />
    </svg>
  );
}

/**
 * Stakeholder Pulse — stakeholder sentiment monitoring
 * A heartbeat/EKG line with a dot.
 * Evokes sensing, pulse-checking, listening.
 */
function StakeholderPulseGlyph({ size }: { size: number }) {
  const s = size;
  const midY = s / 2;
  const pad = s * 0.12;

  // EKG-ish waveform: flat → up → down → spike → flat
  const pts = [
    [pad,         midY],
    [s * 0.3,     midY],
    [s * 0.38,    midY - s * 0.25],
    [s * 0.46,    midY + s * 0.22],
    [s * 0.54,    midY - s * 0.35],
    [s * 0.62,    midY + s * 0.14],
    [s * 0.7,     midY],
    [s - pad,     midY],
  ];
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${s} ${s}`}
      width={s}
      height={s}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d={d}
        stroke="white"
        strokeWidth={s * 0.07}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Pulse dot at the peak */}
      <circle
        cx={s * 0.54}
        cy={midY - s * 0.35}
        r={s * 0.06}
        fill="white"
      />
    </svg>
  );
}

/**
 * Nexus — relationship advisor
 * Three nodes arranged in a triangle, connected by edges.
 * The center node is slightly larger and acts as the hub.
 * Evokes network structure, connection, relationship topology.
 */
function NexusGlyph({ size }: { size: number }) {
  const s = size;
  const cx = s / 2;
  const cy = s / 2;

  // Three nodes: top-center, bottom-left, bottom-right
  const nodeTop   = { x: cx,              y: cy - s * 0.30 };
  const nodeLeft  = { x: cx - s * 0.28,  y: cy + s * 0.20 };
  const nodeRight = { x: cx + s * 0.28,  y: cy + s * 0.20 };
  const nodeR     = s * 0.085;
  const hubR      = s * 0.11;

  return (
    <svg
      viewBox={`0 0 ${s} ${s}`}
      width={s}
      height={s}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Edges connecting nodes */}
      <line
        x1={nodeTop.x}   y1={nodeTop.y}
        x2={nodeLeft.x}  y2={nodeLeft.y}
        stroke="white" strokeWidth={s * 0.065} strokeLinecap="round" opacity="0.85"
      />
      <line
        x1={nodeTop.x}   y1={nodeTop.y}
        x2={nodeRight.x} y2={nodeRight.y}
        stroke="white" strokeWidth={s * 0.065} strokeLinecap="round" opacity="0.85"
      />
      <line
        x1={nodeLeft.x}  y1={nodeLeft.y}
        x2={nodeRight.x} y2={nodeRight.y}
        stroke="white" strokeWidth={s * 0.065} strokeLinecap="round" opacity="0.50"
      />
      {/* Outer nodes */}
      <circle cx={nodeTop.x}   cy={nodeTop.y}   r={nodeR} fill="white" opacity="0.90" />
      <circle cx={nodeLeft.x}  cy={nodeLeft.y}  r={nodeR} fill="white" opacity="0.75" />
      <circle cx={nodeRight.x} cy={nodeRight.y} r={nodeR} fill="white" opacity="0.75" />
      {/* Hub node — center, slightly larger, full opacity */}
      <circle cx={cx} cy={cy} r={hubR} fill="white" opacity="0.95" />
    </svg>
  );
}

/**
 * Inversion Detector — prioritization inversion detection
 * Two opposing arrows (up-right / down-left), creating a tension symbol.
 * Evokes reversal, conflict detection, corrective force.
 */
function InversionGlyph({ size }: { size: number }) {
  const s = size;
  const cx = s / 2;
  const cy = s / 2;
  const pad = s * 0.12;
  const arrowLen = s * 0.3;
  const headLen = s * 0.12;
  const stroke = s * 0.07;

  // Up-right arrow (NE)
  const ue = { x: cx + arrowLen * 0.7, y: cy - arrowLen * 0.7 };
  const us = { x: cx - arrowLen * 0.5, y: cy + arrowLen * 0.5 };

  // Down-left arrow (SW) — mirrors it
  const de = { x: cx - arrowLen * 0.7, y: cy + arrowLen * 0.7 };
  const ds = { x: cx + arrowLen * 0.5, y: cy - arrowLen * 0.5 };

  void pad;

  return (
    <svg
      viewBox={`0 0 ${s} ${s}`}
      width={s}
      height={s}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Up-right arrow */}
      <line x1={us.x} y1={us.y} x2={ue.x} y2={ue.y}
        stroke="white" strokeWidth={stroke} strokeLinecap="round" opacity="0.9" />
      {/* Arrowhead NE */}
      <polyline
        points={`${ue.x - headLen} ${ue.y}, ${ue.x} ${ue.y}, ${ue.x} ${ue.y + headLen}`}
        stroke="white" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"
        fill="none" opacity="0.9"
      />

      {/* Down-left arrow */}
      <line x1={ds.x} y1={ds.y} x2={de.x} y2={de.y}
        stroke="white" strokeWidth={stroke} strokeLinecap="round" opacity="0.55" />
      {/* Arrowhead SW */}
      <polyline
        points={`${de.x + headLen} ${de.y}, ${de.x} ${de.y}, ${de.x} ${de.y - headLen}`}
        stroke="white" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"
        fill="none" opacity="0.55"
      />
    </svg>
  );
}

/**
 * Descriptor — title ↔ description.
 * Two text lines with a spark between them: one side seeds the other.
 */
function IdeaCraftingGlyph({ size }: { size: number }) {
  const s = size;
  const stroke = Math.max(1.2, s * 0.08);
  const cx = s / 2;
  const cy = s / 2;

  return (
    <svg
      viewBox={`0 0 ${s} ${s}`}
      width={s}
      height={s}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Top line (title) */}
      <line
        x1={s * 0.18}
        y1={s * 0.32}
        x2={s * 0.82}
        y2={s * 0.32}
        stroke="white"
        strokeWidth={stroke}
        strokeLinecap="round"
        opacity="0.95"
      />
      {/* Bottom lines (description) */}
      <line
        x1={s * 0.18}
        y1={s * 0.55}
        x2={s * 0.72}
        y2={s * 0.55}
        stroke="white"
        strokeWidth={stroke}
        strokeLinecap="round"
        opacity="0.7"
      />
      <line
        x1={s * 0.18}
        y1={s * 0.7}
        x2={s * 0.58}
        y2={s * 0.7}
        stroke="white"
        strokeWidth={stroke}
        strokeLinecap="round"
        opacity="0.45"
      />
      {/* Spark between the lanes */}
      <circle cx={cx} cy={cy * 0.88} r={s * 0.06} fill="white" opacity="0.95" />
      <line
        x1={cx}
        y1={s * 0.38}
        x2={cx}
        y2={s * 0.48}
        stroke="white"
        strokeWidth={stroke * 0.7}
        strokeLinecap="round"
        opacity="0.85"
      />
    </svg>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

export interface AgentGlyphProps {
  type: AgentGlyphType;
  colorScheme: AgentColorScheme;
  /** Pixel diameter of the circular container. Default: 28 */
  size?: number;
  /** Show the pulsing ring animation (e.g. when the agent is available / active) */
  pulse?: boolean;
  /** Extra className on the outer container */
  className?: string;
}

export function AgentGlyph({
  type,
  colorScheme,
  size = 28,
  pulse = false,
  className = '',
}: AgentGlyphProps) {
  const innerSize = Math.round(size * 0.62);
  const gradient = GRADIENT_CLASSES[colorScheme];
  const ringColor = RING_CLASSES[colorScheme];

  return (
    <span
      className={`relative inline-flex flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${gradient} ${className}`}
      style={{ width: size, height: size }}
    >
      {pulse && (
        <span
          className={`absolute inset-0 rounded-full ring-2 ${ringColor} animate-agent-pulse`}
        />
      )}
      {type === 'atc'               && <AtcGlyph size={innerSize} />}
      {type === 'twins'             && <TwinsGlyph size={innerSize} />}
      {type === 'stakeholder-pulse' && <StakeholderPulseGlyph size={innerSize} />}
      {type === 'inversion'         && <InversionGlyph size={innerSize} />}
      {type === 'nexus'             && <NexusGlyph size={innerSize} />}
      {type === 'idea-crafting'     && <IdeaCraftingGlyph size={innerSize} />}
    </span>
  );
}
